import React, {ReactElement, useMemo, useContext} from 'react';
import {Typography} from '@customink/pigment-react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import {useGetOrderSummaryQuery} from '../../api/storesApi';
import Header from '../Header';
import Footer from '../Footer';
import {OrderSummary} from '../../models/orderSummary';
import OrderConfirmDialog from '../OrderConfirmDialog';
import {MobileContext} from '../../containers/MobileProvider';
import {formatCurrency} from '../../utils/helpers';
import './index.scss';

type OrderSummaryPageProps = {
  storeSlug: string;
  campaignId: string;
};

const ORDER_SUMMARY_ITEM_KEYS = [
  'totalItemsQuantity',
  'subtotal',
  'shipping',
  'tax',
  'totalCollectedOnline'
];

const ORDER_SUMMARY_ITEM_LABELS = [
  'Number of Items',
  'Subtotal',
  'Shipping',
  'Tax',
  'Estimated Total'
];

type OrderSummaryKey = keyof OrderSummary;

const OrderSummaryPlaceholder: React.FC = () => {
  const mock = [1, 2, 3, 4, 5];
  return (
    <div className="orderSummaryPlaceholder">
      {mock.map(
        (row: number): ReactElement => (
          <Skeleton className="skeletonText" variant="text" key={row} />
        )
      )}
    </div>
  );
};

const OrderListPlaceholder: React.FC = () => {
  const mock = [1, 2];
  return (
    <>
      {mock.map(
        (row: number): ReactElement => (
          <div className="orderListPlaceholder" key={row}>
            <Skeleton className="skeletonText" variant="text" width={200} />
            <div>
              <Skeleton className="skeletonBox" variant="rectangular" />
              <div className="skeletonInfo">
                <Skeleton className="skeletonText" variant="text" />
                <Skeleton className="skeletonText" variant="text" />
                <Skeleton className="skeletonText" variant="text" />
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

const OrderSummaryPage: React.FC<OrderSummaryPageProps> = ({
  storeSlug,
  campaignId
}: OrderSummaryPageProps): ReactElement => {
  const {data: orderSummaryState, isLoading} = useGetOrderSummaryQuery({
    urlSlug: storeSlug,
    campaignId
  });

  const orderList = useMemo(() => {
    if (isLoading || !orderSummaryState?.purchases.length) {
      return <OrderListPlaceholder />;
    }
    return (
      <>
        {orderSummaryState.purchases?.map(item => {
          return (
            item.status === 'active' && ( // To be removed once the api begins filtering purchases to active.
              <div className="orderedItem" key={item.referenceId}>
                <Typography
                  variant="h6"
                  className="customerName">{`${item.firstName} ${item.lastName}`}</Typography>
                {item.purchaseItemsSummary.map(purchase => {
                  return (
                    <div
                      className="orderedItem-inner"
                      key={`${item.referenceId}${purchase.productName}${purchase.color}${purchase.color}`}>
                      <div className="orderedItem-inner-img">
                        <img
                          src={purchase?.productImage}
                          alt={purchase.productName}
                        />
                      </div>
                      <div className="orderedItem-inner-info">
                        <Typography variant="subHeading" className="itemTitle">
                          {`${purchase.productName} - ${purchase.color}`}
                        </Typography>
                        <div className="orderedItem-inner-info-detail">
                          <div className="orderedItem-details">
                            <Typography>
                              <strong>Sizes</strong>
                            </Typography>
                            <Typography className="itemDetail">
                              {`${purchase.size}: ${
                                purchase.quantity
                              } - ${formatCurrency(
                                Number(purchase.unitPrice)
                              )}`}
                            </Typography>
                          </div>
                          <div className="orderedItem-details">
                            <Typography>
                              <strong>Qty</strong>
                            </Typography>
                            <Typography className="itemDetail">{`${purchase.quantity}`}</Typography>
                          </div>
                          <div className="orderedItem-details">
                            <Typography>
                              <strong>Total</strong>
                            </Typography>
                            <Typography className="bolder itemDetail">
                              {formatCurrency(Number(purchase.subtotal))}
                            </Typography>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          );
        })}
      </>
    );
  }, [isLoading]);

  const orderSummary = useMemo(() => {
    if (isLoading || !orderSummaryState) {
      return <OrderSummaryPlaceholder />;
    }
    return (
      <div className="summary-inner">
        <Typography variant="h6" className="summary-title">
          <strong>Order Summary</strong>
        </Typography>
        {ORDER_SUMMARY_ITEM_KEYS.map((key: OrderSummaryKey, index) => {
          let lastLine = false;
          if (index === Object.keys(orderSummaryState).length - 2) {
            lastLine = true;
          }
          const value = orderSummaryState[key] as number;
          return (
            <Typography
              key={key}
              variant={`${lastLine ? 'h6' : 'bodyLong2'}`}
              className={`summary-item${lastLine ? '-divider' : ``}`}>
              {ORDER_SUMMARY_ITEM_LABELS[index]}:
              <span>
                {key.includes('Items')
                  ? orderSummaryState[key]
                  : `$${value.toFixed(2)}`}
              </span>
            </Typography>
          );
        })}
        <OrderConfirmDialog storeSlug={storeSlug} />
      </div>
    );
  }, [orderSummaryState, isLoading]);
  const mobile = useContext(MobileContext);
  const orderSummaryClasses = `orderSummary${mobile ? ' mobile' : ''}`;

  return (
    <div className={orderSummaryClasses}>
      <Header storeSlug={storeSlug} />
      <Container className="orderSummary-body">
        <Typography variant="h5" className="page-title">
          Order Summary
        </Typography>
        <div className="orderSummary-body-inner">
          <Grid container spacing={2} className="gridContainer">
            <Grid item xs={12} sm={12} md={7} lg={7}>
              <Paper elevation={0} className="orderedItemList">
                {orderList}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <Paper elevation={0} square className="summary">
                {orderSummary}
              </Paper>
            </Grid>
          </Grid>
        </div>
      </Container>
      <Footer />
    </div>
  );
};

export default OrderSummaryPage;
